import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PushNotificationToggle = ({ workerId, customerEmail }) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setLoading(false);
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error("Error checking subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribe = async () => {
    setToggling(true);
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast.error("Notification permission denied");
        setToggling(false);
        return;
      }

      // Get VAPID key
      const { data: { publicKey } } = await axios.get(`${API}/push/vapid-key`);

      // Subscribe to push
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to server
      await axios.post(`${API}/push/subscribe`, {
        worker_id: workerId || null,
        customer_email: customerEmail || null,
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey("p256dh")))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey("auth"))))
        }
      });

      setIsSubscribed(true);
      toast.success("Push notifications enabled!");
    } catch (error) {
      console.error("Error subscribing:", error);
      toast.error("Failed to enable notifications");
    } finally {
      setToggling(false);
    }
  };

  const unsubscribe = async () => {
    setToggling(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await axios.delete(`${API}/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`);
      }

      setIsSubscribed(false);
      toast.success("Push notifications disabled");
    } catch (error) {
      console.error("Error unsubscribing:", error);
      toast.error("Failed to disable notifications");
    } finally {
      setToggling(false);
    }
  };

  // Check if browser supports push notifications
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return null;
  }

  if (loading) {
    return (
      <Button variant="outline" size="sm" disabled className="rounded-full">
        <Loader2 className="w-4 h-4 animate-spin" />
      </Button>
    );
  }

  return (
    <Button
      variant={isSubscribed ? "default" : "outline"}
      size="sm"
      onClick={isSubscribed ? unsubscribe : subscribe}
      disabled={toggling}
      className={`rounded-full ${isSubscribed ? "bg-[#1a73e8] hover:bg-[#1557b0]" : ""}`}
      data-testid="push-notification-toggle"
    >
      {toggling ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isSubscribed ? (
        <>
          <Bell className="w-4 h-4 mr-2" />
          Notifications On
        </>
      ) : (
        <>
          <BellOff className="w-4 h-4 mr-2" />
          Enable Notifications
        </>
      )}
    </Button>
  );
};

export default PushNotificationToggle;
